/* eslint-disable react/jsx-props-no-spreading, react/destructuring-assignment, @typescript-eslint/no-explicit-any */
import React, { ReactElement } from 'react'
import moment from 'moment'
import 'moment/locale/ar-tn'
import {
  Document,
  Page,
  Font,
  StyleSheet,
  Image,
  Text,
  View,
} from '@react-pdf/renderer'
import { Certification } from '../../types/certification'
import { User } from '../../types/user'
import locations from '../../constants/pdf-generator/locations'
import certif from '../../assets/images/certif.png'

// --------- Font ---------
Font.register({
  family: 'ubuntu',
  fonts: [
    {
      src: 'https://res.cloudinary.com/enigma-dev/raw/upload/v1654439777/L1/ElMessiri-SemiBold_czvriz.ttf',
    },
  ],
})

// --------- Helpers ---------
const S = (v: unknown) => (v == null ? '' : String(v))

const fontSize = 20
const infoFontSize = fontSize + 2

// --------- Styles ---------
const styles = StyleSheet.create({
  page: {
    position: 'relative',
    backgroundColor: 'white',
  },
  // Image de fond plein écran
  bg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  // Calque pour le texte
  layer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },

  text: {
    position: 'absolute',
    top: 100,
    fontFamily: 'ubuntu',
    fontSize: 40,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  Coretext: {
    position: 'absolute',
    top: 180,
    fontFamily: 'ubuntu',
    fontSize,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  Coretext2: {
    position: 'absolute',
    top: 360,
    fontFamily: 'ubuntu',
    fontSize,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  Coretext3: {
    position: 'absolute',
    top: 400,
    fontFamily: 'ubuntu',
    fontSize,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  trainingTitle: {
    color: 'red',
    position: 'absolute',
    top: 320,
    fontFamily: 'ubuntu',
    fontSize,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  Signature1: {
    position: 'absolute',
    top: 450,
    right: 100,
    fontFamily: 'ubuntu',
    fontSize: fontSize - 7,
  },
  SignatureName: {
    position: 'absolute',
    top: 470,
    right: 140,
    fontFamily: 'ubuntu',
    fontSize: fontSize - 7,
    color: 'red',
  },
  CoretextInfoName: {
    position: 'absolute',
    top: 220,
    fontFamily: 'ubuntu',
    fontSize: 15,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  CoretextInfoColoredName: {
    fontFamily: 'ubuntu',
    fontSize: infoFontSize,
    color: 'red',
  },
  CoretextInfoPlace: {
    position: 'absolute',
    top: 270,
    fontFamily: 'ubuntu',
    fontSize,
    alignSelf: 'center',
    textAlign: 'center',
    width: '100%',
  },
  CoretextInfoColoredPlace: {
    fontFamily: 'ubuntu',
    fontSize: infoFontSize,
    color: 'red',
  },
  certifId: {
    position: 'absolute',
    top: 360,
    left: -65,
    transform: 'rotate(-90deg)',
    fontSize: 10,
    fontFamily: 'ubuntu',
    letterSpacing: 2,
  },
})

// --------- Props ---------
interface PdfProps {
  certification: Certification | undefined
  connectedUser: Omit<User, 'certifications'> | undefined
}

// --------- Component ---------
const MyDocument = ({
  certification,
  connectedUser,
}: PdfProps): ReactElement => {
  const certifDate = moment(certification?.date ?? new Date()).locale('ar-tn')
  const year = certifDate.format('YYYY')

  const llll = certifDate.format('LLLL')
  const parts = llll.split(' ')
  const weekDay = S(parts[0] ?? '')
  const day = S(parts.find((p) => /^\d+$/.test(p)) ?? '')
  const month = S(
    parts.find((p) => /[^\d]/.test(p) && p !== weekDay) ??
      certifDate.format('MMMM')
  )

  const locCode = S(connectedUser?.location)
  const locLabel = (locations as Record<string, string>)[locCode] ?? 'الكشافة'

  const fullName = connectedUser
    ? `${S(connectedUser.firstName)} ${S(connectedUser.lastName)}`.trim()
    : ''

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="landscape">
        {/* image de fond */}
        <Image src={certif} style={styles.bg} fixed />

        {/* calque texte */}
        <View style={styles.layer}>
          <Text style={styles.text}>شهادة مشاركة</Text>

          <Text style={styles.Coretext}>
            يشهد رئيس اللجنة الوطنية لتنمية القيادات بالكشافة التونسية أن
          </Text>

          <Text style={styles.CoretextInfoName}>
            <Text style={styles.CoretextInfoColoredPlace}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {locLabel}
            </Text>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;بجهة :&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Text style={styles.CoretextInfoColoredName}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {fullName}
            </Text>
            القائد)ة( :&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </Text>

          <Text style={styles.trainingTitle}>
            &nbsp;&nbsp;{S(certification?.training?.title)}&nbsp;&nbsp;
          </Text>

          <Text style={styles.Coretext2}>
            <Text style={styles.Coretext3}> و بعد إتمام جميع متطلباتها </Text>
            <Text style={styles.CoretextInfoColoredPlace}>
              &nbsp;&nbsp;&nbsp;
              {year}&nbsp;
              {weekDay}&nbsp;
              {day}&nbsp;
              {month}&nbsp;
            </Text>
            و ذلك بتاريخ&nbsp;&nbsp;&nbsp;
          </Text>

          <Text style={styles.CoretextInfoPlace}>
            <Text style={styles.CoretextInfoColoredPlace}>
              &nbsp;&nbsp;{S(certification?.training?.code)}&nbsp;&nbsp;
            </Text>
            قد إجتاز بنجاح الوحدة التأهيلية عن بعد
          </Text>

          <Text style={styles.Signature1}>
            رئيس اللجنة الوطنية لتنمية القيادات
          </Text>
          <Text style={styles.SignatureName}>ق / عبد الوهاب بن اسماعيل</Text>

          <Text style={styles.certifId}>{S(certification?.id)}</Text>
        </View>
      </Page>
    </Document>
  )
}

export default MyDocument
